import { createFileRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';
import { allowedConnectOptionsSchema } from '../../../constants/common';
import { runAddChannelAuthFlow } from '../../../api/connectPlatformAccount';
import { Box, Button, Text } from '@streamelements/frontend-ui';
import { CompositionsService } from '../../../services/CompositionsService';
import { DestinationMetadataApiClient } from '../../../services/DestinationsApiClient';
import { openVideoCompositionDockingWidget } from '../../../utils/dockingWidgets';
import { useTimeout } from 'react-use';

const searchValidation = z.object({
  provider: allowedConnectOptionsSchema,
  createVerticalComposition: z.coerce.boolean().default(false),
});

export const Route = createFileRoute('/_authenticated/onboarding/setup-multistreaming/_layout/connect')({
  component: ConnectProvider,
  pendingComponent: () => <PendingConnectProvider />,
  validateSearch: searchValidation,
  loaderDeps: ({ search: { provider, createVerticalComposition } }) => ({
    provider,
    createVerticalComposition,
  }),
  loader: async ({
    context: { authContextProvider, serviceEnvironment },
    deps: { provider, createVerticalComposition },
  }) => {
    const destinationMetadata = await runAddChannelAuthFlow({
      authContextProvider,
      serviceEnvironment,
      provider,
    });

    if (!destinationMetadata) {
      throw new Error('Failed to connect provider');
    }

    if (createVerticalComposition) {
      const compositions = await CompositionsService.getActiveCompositions();

      let [verticalComposition] = Object.values(compositions).filter((c) => {
        const { height, width } = c.videoFrame;
        return height > width;
      });

      if (!verticalComposition) {
        verticalComposition = await CompositionsService.createVideoComposition({
          videoCompositionName: 'Vertical',
          videoFrame: { width: 1080, height: 1920 },
        });
      }

      const destinationMetadataApiClient = new DestinationMetadataApiClient({
        destinationMetadata,
        authContextProvider,
        serviceEnvironment,
      });

      destinationMetadataApiClient.updateDestination({
        outputs: destinationMetadataApiClient.get().outputs.map((output, position, array) => {
          if (position < array.length - 1) {
            return output;
          }

          // We want to add the vertical composition as the last output
          return {
            ...output,
            videoCompositionId: verticalComposition.id,
          };
        }),
      });

      await openVideoCompositionDockingWidget({
        videoCompositionId: verticalComposition.id,
      });
    }

    return redirect({
      to: '/onboarding/setup-multistreaming/success',
      search: {
        provider,
        destinationId: destinationMetadata.id,
      },
    });
  },
});

function ConnectProvider() {
  return <div>Hello /onboarding/setup-multistreaming/_layout/connect/$provider!</div>;
}

function PendingConnectProvider() {
  const [showManualLink] = useTimeout(5000);

  return (
    <Box css={{ px: '1rem', flex: 1 }}>
      <Text.Subtitle weight="bold" css={{ mb: '0.25rem', mt: '1rem' }}>
        Connecting...
      </Text.Subtitle>
      <Text.Body variant="caption" css={{ color: '$textPrimary70', mb: '1rem', maxWidth: 280, mx: 'auto' }}>
        We're connecting your account. Please follow the instructions in the new browser window.
      </Text.Body>

      {showManualLink() && (
        <>
          <Text.Body
            variant="caption"
            css={{
              color: '$textPrimary70',
              mb: '1rem',
              maxWidth: 280,
              mx: 'auto',
            }}
          >
            If the external browser window didn't open, please try again.
          </Text.Body>

          <Button density="compact" color="neutral" onClick={() => window.location.reload()}>
            Try again
          </Button>
        </>
      )}
    </Box>
  );
}
